import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

interface GradingSummary {
  grading_event_id: number;
  grading_date: string;
  grading_event_desc: string;
  grading_type: string;
  dojo_name: string;
  total_candidates: number;
  passed: number;
  probation: number;
  resit: number;
  cancelled: number;
  pass_rate_pct: number;
  avg_total_score: number;
}

interface GradingResult {
  grading_id: number;
  grading_event_id: number;
  grading_date: string;
  grading_event_desc: string;
  grading_type: string;
  member_number: string;
  member_name: string;
  dojo_name: string;
  instructor_name: string;
  rank_before: string;
  target_rank: string;
  kata: number;
  kihon: number;
  kumite: number;
  total_score: number;
  kata_result: string;
  kihon_result: string;
  kumite_result: string;
  grading_status: string;
  examiner_comment: string;
  grading_fee: number;
}

type ViewMode = 'summary' | 'detail';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;
const PASS_MARK = 12;

const generateYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear + 1; y >= 2017; y--) {
    years.push(y);
  }
  return years;
};

const YEARS = generateYears();

const Gradings: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [summary, setSummary] = useState<GradingSummary[]>([]);
  const [allDetail, setAllDetail] = useState<GradingResult[]>([]);
  const [filtered, setFiltered] = useState<GradingResult[]>([]);
  const [dojos, setDojos] = useState<string[]>([]);
  const [ranks, setRanks] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [sortField, setSortField] = useState<keyof GradingResult>('grading_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedDojos, setSelectedDojos] = useState<string[]>([]);
  const [selectedRanks, setSelectedRanks] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const fetchSummary = useCallback(async (years: number[]) => {
    try {
      setLoadingSummary(true);
      setSummaryError('');
      const yearParam = years.length > 0 ? '?years=' + years.join(',') : '';
      const res = await api.get('/reports/gradings/summary' + yearParam);
      setSummary(res.data.data as GradingSummary[]);
    } catch (err) {
      setSummaryError('Failed to load grading summary — please try again');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchDetail = useCallback(async (years: number[]) => {
    try {
      setLoadingDetail(true);
      setDetailError('');
      const yearParam = years.length > 0 ? '&years=' + years.join(',') : '';
      const res = await api.get('/reports/gradings/results?limit=2000' + yearParam);
      const data = res.data.data as GradingResult[];
      setAllDetail(data);
      setFiltered(data);
      setTotalCount(data.length);
      setDojos(Array.from(new Set(data.map((g) => g.dojo_name))).sort());
      setRanks(Array.from(new Set(data.map((g) => g.target_rank))).sort());
    } catch (err) {
      setDetailError('Failed to load grading results — please try again');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(selectedYears);
    fetchDetail(selectedYears);
  }, [selectedYears, fetchSummary, fetchDetail]);

  useEffect(() => {
    let result = allDetail;

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (g) =>
          (g.member_name || '').toLowerCase().includes(s) ||
          (g.member_number || '').toLowerCase().includes(s) ||
          (g.dojo_name || '').toLowerCase().includes(s) ||
          (g.target_rank || '').toLowerCase().includes(s) ||
          (g.grading_event_desc || '').toLowerCase().includes(s)
      );
    }

    if (selectedDojos.length > 0) {
      result = result.filter((g) => selectedDojos.includes(g.dojo_name));
    }

    if (selectedRanks.length > 0) {
      result = result.filter((g) => selectedRanks.includes(g.target_rank));
    }

    if (selectedStatuses.length > 0) {
      result = result.filter((g) => selectedStatuses.includes(g.grading_status));
    }

    result = [...result].sort((a, b) => {
      const av = a[sortField] !== undefined ? a[sortField] : '';
      const bv = b[sortField] !== undefined ? b[sortField] : '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    setFiltered(result);
    setTotalCount(result.length);
    setPage(1);
  }, [search, selectedDojos, selectedRanks, selectedStatuses, sortField, sortDir, allDetail]);

  const toggleSort = (field: keyof GradingResult) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field: keyof GradingResult) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters = search || selectedDojos.length > 0 || selectedRanks.length > 0 || selectedStatuses.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedDojos([]);
    setSelectedRanks([]);
    setSelectedStatuses([]);
    setPage(1);
  };

  const yearLabel =
    selectedYears.length === 0 ? 'All Years' :
    selectedYears.length === 1 ? selectedYears[0].toString() :
    selectedYears.length + ' years';

  // Grand totals for summary
  const grandTotal = summary.reduce(
    (acc, g) => ({
      total_candidates: acc.total_candidates + Number(g.total_candidates),
      passed: acc.passed + Number(g.passed),
      probation: acc.probation + Number(g.probation),
      resit: acc.resit + Number(g.resit),
      cancelled: acc.cancelled + Number(g.cancelled),
    }),
    { total_candidates: 0, passed: 0, probation: 0, resit: 0, cancelled: 0 }
  );

  const exportExcel = () => {
    setExporting(true);
    try {
      const headers = viewMode === 'summary'
        ? ['Date', 'Event', 'Type', 'Dojo', 'Candidates', 'Passed', 'Probation', 'Resit', 'Cancelled', 'Pass Rate %', 'Avg Score']
        : ['Member No.', 'Member Name', 'Dojo', 'Grading Date', 'Event', 'Rank Before', 'Target Rank', 'Kata', 'Kihon', 'Kumite', 'Total', 'Status', 'Fee (₦)', 'Comment'];

      const rows = viewMode === 'summary'
        ? summary.map((g) => [
            new Date(g.grading_date).toLocaleDateString('en-GB'),
            g.grading_event_desc || '', g.grading_type, g.dojo_name,
            g.total_candidates, g.passed, g.probation, g.resit, g.cancelled,
            g.pass_rate_pct + '%', g.avg_total_score,
          ])
        : filtered.map((g) => [
            g.member_number || '', g.member_name, g.dojo_name,
            new Date(g.grading_date).toLocaleDateString('en-GB'),
            g.grading_event_desc || '', g.rank_before || '', g.target_rank,
            g.kata, g.kihon, g.kumite, g.total_score,
            g.grading_status, g.grading_fee || '', g.examiner_comment || '',
          ]);

      const bom = '\uFEFF';
      const csv = bom + [headers, ...rows]
        .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NASK_Gradings_' + viewMode + '_' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const statusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'success': return styles.statusSuccess;
      case 'probation': return styles.statusProbation;
      case 'resit': return styles.statusResit;
      case 'cancelled': return styles.statusCancelled;
      case 'pending': return styles.statusPending;
      default: return styles.statusPending;
    }
  };

  const scoreStyle = (score: number): React.CSSProperties => ({
    fontWeight: '600',
    color: score >= PASS_MARK ? '#22c55e' : '#cc0000',
  });

  const MultiSelect: React.FC<{
    label: string;
    id: string;
    options: string[];
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }> = ({ label, id, options, selected, setSelected }) => {
    const isOpen = openDropdown === id;
    return (
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>{label}</label>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
            style={{ ...styles.multiSelectBtn, borderColor: selected.length > 0 ? '#cc0000' : '#3a3a3a' }}
          >
            <span>{selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : selected.length + ' selected'}</span>
            <span style={{ fontSize: '10px' }}>▼</span>
          </button>
          {isOpen && (
            <div style={styles.multiSelectDropdown} onClick={(e) => e.stopPropagation()}>
              {options.map((opt) => (
                <label key={opt} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleMulti(opt, setSelected)}
                    style={{ marginRight: '8px', accentColor: '#cc0000' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} onClick={() => setOpenDropdown(null)}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Grading Results</h1>
          <p style={styles.subtitle}>
            {viewMode === 'summary'
              ? summary.length + ' grading events — ' + yearLabel
              : totalCount.toLocaleString() + ' results — ' + yearLabel}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          {/* Grading Year Filter */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Grading Year</label>
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === 'year' ? null : 'year'); }}
                style={{ ...styles.multiSelectBtn, minWidth: '160px', borderColor: selectedYears.length > 0 ? '#cc0000' : '#3a3a3a' }}
              >
                <span>{yearLabel}</span>
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>
              {openDropdown === 'year' && (
                <div
                  style={{ ...styles.multiSelectDropdown, minWidth: '160px', right: 0, left: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {YEARS.map((y) => (
                    <label key={y} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={selectedYears.includes(y)}
                        onChange={() =>
                          setSelectedYears((prev) =>
                            prev.includes(y) ? prev.filter((v) => v !== y) : [...prev, y]
                          )
                        }
                        style={{ marginRight: '8px', accentColor: '#cc0000' }}
                      />
                      {y}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={exportExcel}
            style={exporting ? styles.exportBtnDisabled : styles.exportBtn}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : '⬇️ Export to Excel'}
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div style={styles.viewToggle}>
        <button onClick={() => setViewMode('summary')} style={viewMode === 'summary' ? styles.toggleBtnActive : styles.toggleBtn}>
          📊 Event Summary
        </button>
        <button onClick={() => setViewMode('detail')} style={viewMode === 'detail' ? styles.toggleBtnActive : styles.toggleBtn}>
          🥋 Individual Results
        </button>
      </div>

      {viewMode === 'summary' ? (
        loadingSummary ? (
          <div style={styles.loadingBox}>Loading grading summary...</div>
        ) : summaryError ? (
          <div style={styles.errorBox}>
            {summaryError}
            <button onClick={() => fetchSummary(selectedYears)} style={styles.retryBtn}>Retry</button>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Total Candidates</p>
                <p style={{ ...styles.statValue, color: '#3b82f6' }}>{grandTotal.total_candidates.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Passed</p>
                <p style={{ ...styles.statValue, color: '#22c55e' }}>{grandTotal.passed.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Probation</p>
                <p style={{ ...styles.statValue, color: '#f59e0b' }}>{grandTotal.probation.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Resit</p>
                <p style={{ ...styles.statValue, color: '#cc0000' }}>{grandTotal.resit.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Cancelled</p>
                <p style={{ ...styles.statValue, color: '#666666' }}>{grandTotal.cancelled.toLocaleString()}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Overall Pass Rate</p>
                <p style={{ ...styles.statValue, color: '#6b8cff' }}>
                  {grandTotal.total_candidates > 0
                    ? Math.round((grandTotal.passed / grandTotal.total_candidates) * 100) + '%'
                    : '0%'}
                </p>
              </div>
            </div>

            {/* Summary Table */}
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Date', 'Event', 'Type', 'Dojo', 'Candidates', 'Passed', 'Probation', 'Resit', 'Cancelled', 'Pass Rate', 'Avg Score'].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.map((g, i) => (
                    <tr key={g.grading_event_id + '_' + g.dojo_name} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}>{new Date(g.grading_date).toLocaleDateString('en-GB')}</td>
                      <td style={{ ...styles.td, color: '#ffffff', fontWeight: '500' }}>{g.grading_event_desc || '—'}</td>
                      <td style={styles.td}>
                        <span style={g.grading_type === 'main' ? styles.typeMain : styles.typeMakeup}>
                          {g.grading_type}
                        </span>
                      </td>
                      <td style={styles.td}>{g.dojo_name}</td>
                      <td style={styles.tdNum}>{Number(g.total_candidates)}</td>
                      <td style={{ ...styles.tdNum, color: '#22c55e' }}>{Number(g.passed)}</td>
                      <td style={{ ...styles.tdNum, color: '#f59e0b' }}>{Number(g.probation)}</td>
                      <td style={{ ...styles.tdNum, color: '#cc0000' }}>{Number(g.resit)}</td>
                      <td style={{ ...styles.tdNum, color: '#666666' }}>{Number(g.cancelled)}</td>
                      <td style={styles.tdNum}>
                        <div style={styles.progressBar}>
                          <div style={{
                            ...styles.progressFill,
                            width: g.pass_rate_pct + '%',
                            backgroundColor: Number(g.pass_rate_pct) >= 70 ? '#22c55e' : Number(g.pass_rate_pct) >= 40 ? '#f59e0b' : '#cc0000',
                          }} />
                          <span style={styles.progressLabel}>{g.pass_rate_pct}%</span>
                        </div>
                      </td>
                      <td style={styles.tdNum}>{Number(g.avg_total_score).toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={styles.footerRow}>
                    <td colSpan={4} style={{ ...styles.td, fontWeight: '700', color: '#ffffff' }}>TOTAL</td>
                    <td style={styles.tdNum}>{grandTotal.total_candidates}</td>
                    <td style={{ ...styles.tdNum, color: '#22c55e' }}>{grandTotal.passed}</td>
                    <td style={{ ...styles.tdNum, color: '#f59e0b' }}>{grandTotal.probation}</td>
                    <td style={{ ...styles.tdNum, color: '#cc0000' }}>{grandTotal.resit}</td>
                    <td style={{ ...styles.tdNum, color: '#666666' }}>{grandTotal.cancelled}</td>
                    <td style={styles.tdNum}>
                      {grandTotal.total_candidates > 0
                        ? Math.round((grandTotal.passed / grandTotal.total_candidates) * 100) + '%'
                        : '0%'}
                    </td>
                    <td style={styles.tdNum}>—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )
      ) : loadingDetail ? (
        <div style={styles.loadingBox}>Loading individual results...</div>
      ) : detailError ? (
        <div style={styles.errorBox}>
          {detailError}
          <button onClick={() => fetchDetail(selectedYears)} style={styles.retryBtn}>Retry</button>
        </div>
      ) : (
        <>
          {/* Detail Filters */}
          <div style={styles.filtersCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.filtersRow}>
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Search</label>
                <input
                  type="text"
                  placeholder="Name, member no., dojo, rank..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              <MultiSelect label="Dojo" id="dojo" options={dojos} selected={selectedDojos} setSelected={setSelectedDojos} />
              <MultiSelect label="Target Rank" id="rank" options={ranks} selected={selectedRanks} setSelected={setSelectedRanks} />
              <MultiSelect
                label="Result"
                id="status"
                options={['success', 'probation', 'resit', 'pending', 'cancelled']}
                selected={selectedStatuses}
                setSelected={setSelectedStatuses}
              />
              {hasFilters && (
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>&nbsp;</label>
                  <button onClick={clearFilters} style={styles.clearBtn}>Clear All</button>
                </div>
              )}
            </div>
          </div>

          {/* Detail Table */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {[
                    { label: 'Date', field: 'grading_date' },
                    { label: 'Member', field: 'member_name' },
                    { label: 'Dojo', field: 'dojo_name' },
                    { label: 'Rank Before', field: 'rank_before' },
                    { label: 'Target Rank', field: 'target_rank' },
                    { label: 'Kata', field: 'kata' },
                    { label: 'Kihon', field: 'kihon' },
                    { label: 'Kumite', field: 'kumite' },
                    { label: 'Total', field: 'total_score' },
                    { label: 'Result', field: 'grading_status' },
                    { label: 'Fee', field: 'grading_fee' },
                  ].map((col) => (
                    <th key={col.field} style={styles.th} onClick={() => toggleSort(col.field as keyof GradingResult)}>
                      {col.label}
                      <span style={styles.sortIcon}>{sortIcon(col.field as keyof GradingResult)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((g, i) => (
                  <tr key={g.grading_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}>{new Date(g.grading_date).toLocaleDateString('en-GB')}</td>
                    <td style={{ ...styles.td, fontWeight: '500', color: '#ffffff' }}>
                      <div>{g.member_name}</div>
                      <div style={{ fontSize: '11px', color: '#666666' }}>{g.member_number || ''}</div>
                    </td>
                    <td style={styles.td}>{g.dojo_name}</td>
                    <td style={styles.td}>{g.rank_before || '—'}</td>
                    <td style={styles.td}>
                      <span style={styles.rankBadge}>{g.target_rank}</span>
                    </td>
                    <td style={{ ...styles.tdNum, ...scoreStyle(g.kata) }}>{g.kata}</td>
                    <td style={{ ...styles.tdNum, ...scoreStyle(g.kihon) }}>{g.kihon}</td>
                    <td style={{ ...styles.tdNum, ...scoreStyle(g.kumite) }}>{g.kumite}</td>
                    <td style={{ ...styles.tdNum, fontWeight: '700', color: g.total_score >= PASS_MARK * 3 ? '#22c55e' : '#f59e0b' }}>
                      {g.total_score}
                    </td>
                    <td style={styles.td}>
                      <span style={statusStyle(g.grading_status)}>{g.grading_status}</span>
                    </td>
                    <td style={styles.tdNum}>
                      {g.grading_fee ? '₦' + Number(g.grading_fee).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginated.length === 0 && (
              <div style={styles.emptyBox}>No results found matching your filters.</div>
            )}
          </div>

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <div style={styles.pagination}>
              <span style={styles.pageInfo}>
                {'Showing ' + ((page - 1) * PAGE_SIZE + 1).toLocaleString() + '–' + Math.min(page * PAGE_SIZE, totalCount).toLocaleString() + ' of ' + totalCount.toLocaleString() + ' results'}
              </span>
              <div style={styles.pageButtons}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>First</button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}>Prev</button>
                <span style={styles.pageNumber}>{'Page ' + page + ' of ' + totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Next</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}>Last</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '32px', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' },
  title: { color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' },
  subtitle: { color: '#888888', fontSize: '14px', margin: '0' },
  exportBtn: { backgroundColor: '#1a3a1a', border: '1px solid #22c55e', color: '#22c55e', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  exportBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#666666', padding: '10px 20px', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px' },
  viewToggle: { display: 'flex', gap: '8px', marginBottom: '24px' },
  toggleBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#888888', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  toggleBtnActive: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#ffffff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px' },
  statLabel: { color: '#888888', fontSize: '12px', margin: '0 0 8px 0', fontWeight: '500' },
  statValue: { fontSize: '28px', fontWeight: '700', margin: '0' },
  retryBtn: { marginLeft: '12px', backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { backgroundColor: '#2d1a1a', border: '1px solid #cc3333', color: '#ff6666', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center' },
  loadingBox: { color: '#888888', textAlign: 'center', padding: '48px' },
  tableWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', borderRadius: '12px', border: '1px solid #2a2a2a', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a1a1a', color: '#888888', padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, cursor: 'pointer', userSelect: 'none' },
  sortIcon: { color: '#cc0000', fontSize: '10px' },
  trEven: { backgroundColor: '#0f0f0f' },
  trOdd: { backgroundColor: '#111111' },
  td: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' },
  tdNum: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap', textAlign: 'right' },
  footerRow: { backgroundColor: '#1a1a1a', borderTop: '2px solid #3a3a3a' },
  progressBar: { position: 'relative', backgroundColor: '#2a2a2a', borderRadius: '4px', height: '20px', minWidth: '100px', overflow: 'hidden' },
  progressFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: '4px', transition: 'width 0.3s ease' },
  progressLabel: { position: 'absolute', right: '6px', top: '2px', fontSize: '11px', fontWeight: '600', color: '#ffffff', zIndex: 1 },
  rankBadge: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusSuccess: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusProbation: { backgroundColor: '#2d1a0d', color: '#f59e0b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusResit: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusCancelled: { backgroundColor: '#1a1a1a', color: '#666666', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  statusPending: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  typeMain: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  typeMakeup: { backgroundColor: '#2d1a2d', color: '#cc88ff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  emptyBox: { color: '#888888', textAlign: 'center', padding: '48px', backgroundColor: '#0f0f0f' },
  filtersCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' },
  filterLabel: { color: '#888888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', outline: 'none', minWidth: '220px' },
  multiSelectBtn: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  multiSelectDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', minWidth: '180px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', color: '#cccccc', fontSize: '13px', padding: '6px 8px', cursor: 'pointer', borderRadius: '4px' },
  clearBtn: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { color: '#888888', fontSize: '14px' },
  pageButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  pageBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#ffffff', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  pageBtnDisabled: { backgroundColor: '#111111', border: '1px solid #222222', color: '#444444', padding: '8px 14px', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' },
  pageNumber: { color: '#888888', fontSize: '13px', padding: '0 8px' },
};

export default Gradings;